import { QueryClient, type QueryKey, type UseSuspenseQueryResult, useSuspenseQuery } from "@tanstack/react-query"
import { AxiosError } from "axios"

import { hours, seconds } from "@/3514/utils"

import { EDiagnosticSummaryQueryKey as EKey } from "./diagnostic-summary.api.config"
import adapter from "./diagnostic-summary.api.queries.adapter"
import fetcher from "./diagnostic-summary.api.queries.fetcher"
import type {
    IDiagnosticSummaryQueryDto as IDto,
    IDiagnosticSummaryQueryModel as IModel
} from "./diagnostic-summary.api.types"

type _TParams = { participantId: number }

type TDiagnosticSummarySuspenseQuery = UseSuspenseQueryResult<IModel, AxiosError>
function useDiagnosticSummarySuspenseQuery({
    participantId
}: _TParams): ReturnType<(params: _TParams) => TDiagnosticSummarySuspenseQuery> {
    return useSuspenseQuery<IDto, AxiosError, IModel, QueryKey>({
        queryKey: EKey({ participantId }),
        queryFn: fetcher,
        select: adapter,
        staleTime: hours(2),
        gcTime: hours(1),
        refetchOnWindowFocus: true,
        refetchOnMount: false,
        retry: 1,
        retryDelay: seconds(5),
        networkMode: "online",
        notifyOnChangeProps: ["data"]
    })
}

async function diagnosticSummaryQueryPrefetcher(
    { qc }: { qc: QueryClient },
    { participantId }: { participantId: number }
): Promise<void> {
    await qc.prefetchQuery<IDto, AxiosError, IModel, QueryKey>({
        queryKey: EKey({ participantId }),
        queryFn: fetcher,
        staleTime: hours(2),
        gcTime: hours(1)
    })
}

export { useDiagnosticSummarySuspenseQuery }
export { diagnosticSummaryQueryPrefetcher }
export type { TDiagnosticSummarySuspenseQuery }
